import { type ProgramType } from 'Program';

import { StudentDashboardStateKey } from 'AdmissionsGuidance/StudentDashboard.types';
import { useTranslation } from 'react-i18next';
import { useDateHelper } from 'FrontRoyalAngular/useDateHelper';
import { useMemo, useCallback } from 'react';

type useWelcomeMessageConfigProps = {
    messageIsDismissed: boolean;
    isMobile: boolean;
    studentDashboardStateKey: StudentDashboardStateKey;
    programType: ProgramType;
    brandNameLong: string;
    applicationDeadlineDate: Date | null;
    userName?: string | null;
};

export function useWelcomeMessageConfig({
    messageIsDismissed,
    isMobile,
    studentDashboardStateKey,
    programType,
    brandNameLong,
    applicationDeadlineDate,
    userName,
}: useWelcomeMessageConfigProps) {
    const { t } = useTranslation('back_royal', { keyPrefix: 'welcomeMessage.welcomeMessage' });
    const dateHelper = useDateHelper();

    const firstMessageLocaleKey = useMemo(() => {
        if (messageIsDismissed && !isMobile) return null;

        if ((studentDashboardStateKey !== StudentDashboardStateKey.signup || messageIsDismissed) && isMobile) {
            return 'welcome_student';
        }

        if (!messageIsDismissed && studentDashboardStateKey === StudentDashboardStateKey.signup) {
            return `welcome_message_first_${isMobile ? 'mobile' : 'desktop'}`;
        }

        return null;
    }, [isMobile, messageIsDismissed, studentDashboardStateKey]);

    const secondMessageLocaleKey = useMemo(() => {
        if (messageIsDismissed && !isMobile) return null;

        if (!messageIsDismissed && studentDashboardStateKey === StudentDashboardStateKey.signup) {
            return `welcome_message_second_${isMobile ? 'mobile' : 'desktop'}`;
        }

        return null;
    }, [isMobile, messageIsDismissed, studentDashboardStateKey]);

    const messageText = useCallback(
        (localeKey: string | null) => {
            if (!localeKey) return null;
            return t(localeKey, {
                institutionName: brandNameLong,
                applicationDeadlineDate: dateHelper.formattedUserFacingMonthDayLong(applicationDeadlineDate!, true),
                programType,
                userName,
            });
        },
        [applicationDeadlineDate, brandNameLong, dateHelper, programType, t, userName],
    );

    const textBubbleShown = useMemo(() => {
        if (firstMessageLocaleKey === 'welcome_student') return false;
        return true;
    }, [firstMessageLocaleKey]);

    return {
        firstMessageText: messageText(firstMessageLocaleKey),
        secondMessageText: messageText(secondMessageLocaleKey),
        textBubbleShown,
    };
}
